import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Search, ChevronRight, ChevronDown, Download, CheckCircle, Key, Loader2
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScormIcon = () => (
  <div className="w-11 h-11 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
    <Download className="h-5 w-5 text-violet-500" />
  </div>
);

const ScormRow = ({ mod, expanded, onToggle }) => (
  <div className="border-b border-slate-100 last:border-b-0" data-testid={`scorm-row-${mod.scorm_id}`}>
    <div
      className="flex items-center gap-4 px-5 py-5 hover:bg-slate-50 transition-colors cursor-pointer"
      onClick={onToggle}
    >
      <ScormIcon />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-slate-900">{mod.name}</p>
      </div>
      <div className="flex items-center gap-6 shrink-0">
        <div className="text-right">
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Key className="h-3.5 w-3.5 text-violet-400" />
            <span>Identifiant</span>
          </div>
          <p className="text-sm font-bold text-slate-700">{mod.identifier || mod.scorm_id?.slice(-5) || '—'}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <CheckCircle className="h-3.5 w-3.5 text-violet-400" />
            <span>Statut</span>
          </div>
          <p className="text-sm font-bold text-slate-700">{mod.status === 'active' ? 'Valide' : 'Brouillon'}</p>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
    </div>
    {expanded && (
      <div className="px-5 pb-4 pl-20">
        <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-1">
          {mod.description && <p>{mod.description}</p>}
          <p>Version SCORM : {mod.version || '1.2'}</p>
          {mod.size && <p>Taille : {mod.size}</p>}
          <p>Complétions : {mod.completions || 0}</p>
        </div>
      </div>
    )}
  </div>
);

const ScormPage = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCriteria, setSearchCriteria] = useState('name');
  const [showDialog, setShowDialog] = useState(false);
  const [newModule, setNewModule] = useState({ name: '', version: '1.2', description: '' });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchModules(); }, []);

  const fetchModules = async () => {
    try {
      const res = await fetch(`${API}/scorm`, { credentials: 'include' });
      if (res.ok) setModules(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newModule.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/scorm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newModule)
      });
      if (res.ok) {
        toast.success('Module SCORM créé');
        fetchModules();
        setShowDialog(false);
        setNewModule({ name: '', version: '1.2', description: '' });
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleSearch = () => {
    // Search is already reactive via filtered
  };

  const handleReset = () => {
    setSearchQuery('');
    setSearchCriteria('name');
  };

  const filtered = modules.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (searchCriteria === 'name') return m.name?.toLowerCase().includes(q);
    if (searchCriteria === 'id') return (m.identifier || m.scorm_id || '').toLowerCase().includes(q);
    return m.name?.toLowerCase().includes(q);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="scorm-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>LMS</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-violet-600 font-medium">Modules SCORM</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900">Bibliothèque de modules SCORM</h1>

        <Button
          className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg gap-2"
          onClick={() => setShowDialog(true)}
          data-testid="btn-add-scorm"
        >
          <Plus className="h-4 w-4" />Ajouter un module SCORM
        </Button>

        {/* Search section */}
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Critère</label>
            <select
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={searchCriteria}
              onChange={(e) => setSearchCriteria(e.target.value)}
              data-testid="scorm-search-criteria"
            >
              <option value="name">Nom</option>
              <option value="id">Identifiant</option>
            </select>
          </div>
          <div className="flex-1 max-w-xs">
            <label className="text-xs text-slate-500 block mb-1">Recherche</label>
            <Input
              placeholder="Rechercher un modul..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="scorm-search-input"
            />
          </div>
          <div className="flex items-end gap-2 mt-auto">
            <Button className="bg-violet-500 hover:bg-violet-600 text-white" onClick={handleSearch} data-testid="btn-scorm-search">
              Rechercher
            </Button>
            <Button variant="outline" onClick={handleReset} data-testid="btn-scorm-reset">
              Réinitialiser
            </Button>
          </div>
        </div>

        {/* Module list */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Download className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun module SCORM</h3>
              <p className="text-sm text-slate-500 mb-4">Ajoutez votre premier module SCORM</p>
              <Button className="bg-violet-500 hover:bg-violet-600 text-white" onClick={() => setShowDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />Ajouter un module
              </Button>
            </div>
          ) : (
            <div>
              {filtered.map(mod => (
                <ScormRow
                  key={mod.scorm_id}
                  mod={mod}
                  expanded={expandedId === mod.scorm_id}
                  onToggle={() => setExpandedId(expandedId === mod.scorm_id ? null : mod.scorm_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Create dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Ajouter un module SCORM</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nom du module</Label>
                <Input value={newModule.name} onChange={e => setNewModule(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Module SCORM - Formation" data-testid="scorm-name-input" />
              </div>
              <div>
                <Label>Version SCORM</Label>
                <select className="w-full border rounded-md p-2 text-sm" value={newModule.version} onChange={e => setNewModule(p => ({ ...p, version: e.target.value }))}>
                  <option value="1.2">SCORM 1.2</option>
                  <option value="2004">SCORM 2004</option>
                </select>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={newModule.description} onChange={e => setNewModule(p => ({ ...p, description: e.target.value }))} placeholder="Description du module" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
              <Button className="bg-violet-500 hover:bg-violet-600 text-white" onClick={handleCreate} disabled={saving} data-testid="create-scorm-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ScormPage;
